/**
 * Une ligne de la table /cee/dossiers (maquette).
 * Colonnes : client + mandataire / velos / docs pills / statut / action
 */

import { Link } from 'react-router-dom';
import DocPills from './DocPills.jsx';
import StatusBadge from './StatusBadge.jsx';
import { formatBlockingReason } from '../lib/formatBlockingReason.js';

/**
 * Formate un SIREN pour l affichage (groupes de 3).
 * @param {string | null | undefined} siren
 * @returns {string}
 */
function formatSiren(siren) {
  if (!siren) return '';
  const digits = siren.replace(/\D/g, '');
  if (digits.length !== 9) return siren;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)}`;
}

/**
 * Suggere une action principale a effectuer selon le status du dossier.
 * @param {object} d
 * @returns {{ label: string, link: string }}
 */
function suggestAction(d) {
  switch (d.status) {
    case 'collecte':
      return { label: 'Relancer client', link: 'Mail rapide' };
    case 'pret_generation':
      return { label: 'Generer Devis+AH', link: 'Generer' };
    case 'attente_signature':
      return { label: 'Relancer Docage', link: 'Voir suivi' };
    case 'transmis':
    case 'depot_cee':
      return { label: 'Attente depot CEE', link: 'Suivi Loris' };
    case 'logistique':
      return {
        label: `${d.velosLivres}/${d.velosQuota} livres`,
        link: 'Tournee livreur',
      };
    case 'cloture':
      return { label: 'Termine', link: 'Voir dossier' };
    case 'bloque':
      // P2-A : nettoyer le suffixe technique "was=<status>" avant affichage
      return { label: formatBlockingReason(d.blockingReason) || 'Bloque', link: 'Voir contact' };
    default:
      return { label: 'Voir', link: 'Voir dossier' };
  }
}

/**
 * @param {{ dossier: object }} props
 */
export default function DossierRow({ dossier }) {
  const action = suggestAction(dossier);
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <td className="py-4 px-4">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-[#0f172a]">{dossier.client?.name}</span>
          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${
            dossier.mandataire?.code === 'FEH'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-orange-100 text-orange-700'
          }`}>
            {dossier.mandataire?.code}
          </span>
        </div>
        <div className="text-xs text-[#64748b] mt-0.5">
          {dossier.client?.siren && <>SIREN {formatSiren(dossier.client.siren)} · </>}
          {dossier.client?.city}
        </div>
      </td>
      <td className="py-4 px-4 text-sm text-[#0f172a]">
        {dossier.velosQuota} velos
      </td>
      <td className="py-4 px-4">
        <DocPills
          docs={dossier.docs || {}}
          velosLivres={dossier.velosLivres || 0}
          velosQuota={dossier.velosQuota || 0}
          dossierStatus={dossier.status}
        />
      </td>
      <td className="py-4 px-4">
        <StatusBadge
          status={dossier.status}
          blockingReason={formatBlockingReason(dossier.blockingReason)}
        />
      </td>
      <td className="py-4 px-4">
        <div className="text-sm font-medium text-[#0f172a]">{action.label}</div>
        <Link
          to={`/cee/dossiers/${dossier.id}`}
          className="text-xs text-blue-600 hover:underline cursor-pointer mt-0.5 block"
        >
          {action.link}
        </Link>
      </td>
    </tr>
  );
}
